import { useRef, type PointerEvent as ReactPointerEvent, type MouseEvent } from "react";

/* ============================================================
   Toque longo

   O gesto só ficou disponível porque o menu do navegador saiu do
   caminho (ver `lib/nativo.ts`). Antes, segurar um card abria "copiar
   endereço do link" e não havia gesto para reaproveitar.

   QUEM MANDA AQUI É O `contextmenu`, não um cronômetro nosso. O navegador
   já sabe reconhecer um toque longo — é ele quem dá a vibradinha no
   Android —, e esse evento é o aviso de que reconheceu. Perguntar a ele
   sai de graça e acerta o tempo de cada aparelho.

   O cronômetro só existiu primeiro, e quebrou no PWA instalado: quando o
   navegador assume o gesto ele dispara `pointercancel`, que matava o
   temporizador. Na aba isso caía depois dos 420 ms e dava tempo; no app
   instalado caía antes, e o toque longo simplesmente não acontecia — só a
   vibradinha, que era o navegador confirmando o gesto que a gente estava
   prestes a perder.

   O cronômetro ficou como reserva porque nem toda plataforma dispara
   `contextmenu` no toque longo — o Safari não dispara quando o
   `touch-callout` está desligado, que é justamente o nosso caso.
   ============================================================ */

const ESPERA = 420;
/** Rolar o feed não é segurar. Acima disso o dedo está viajando. */
const TOLERANCIA = 12;

export interface Segurar {
  onPointerDown: (e: ReactPointerEvent) => void;
  onPointerMove: (e: ReactPointerEvent) => void;
  onPointerUp: () => void;
  onPointerCancel: () => void;
  onContextMenu: (e: MouseEvent) => void;
  onClick: (e: MouseEvent) => void;
}

/**
 * Chama `aoSegurar` quando o dedo fica parado por um tempo, e engole o
 * clique que vem depois.
 *
 * Engolir o clique é o ponto: o alvo costuma ser um link, e sem isso
 * segurar o card abriria o prato ao soltar — a pessoa reagiria e seria
 * levada para outra tela no mesmo gesto.
 */
export function useToqueLongo(aoSegurar: () => void): Segurar {
  const relogio = useRef<ReturnType<typeof setTimeout> | null>(null);
  const partiu = useRef<{ x: number; y: number } | null>(null);
  const segurou = useRef(false);

  const parar = () => {
    if (relogio.current) clearTimeout(relogio.current);
    relogio.current = null;
  };

  /* Dois caminhos chegam aqui e nenhum sabe do outro, então a porta
     fecha depois do primeiro. Sem isso, um aparelho que dispare os dois
     abriria a barra duas vezes no mesmo gesto. */
  const abrir = () => {
    if (segurou.current) return;
    segurou.current = true;
    parar();
    aoSegurar();
  };

  return {
    onPointerDown: (e) => {
      // Só o toque e o botão principal. Botão do meio e caneta com
      // clique secundário têm significados próprios no sistema.
      if (e.button !== 0) return;
      segurou.current = false;
      partiu.current = { x: e.clientX, y: e.clientY };
      parar();
      relogio.current = setTimeout(abrir, ESPERA);
    },
    onPointerMove: (e) => {
      const inicio = partiu.current;
      if (!inicio || !relogio.current) return;
      const longe = Math.abs(e.clientX - inicio.x) > TOLERANCIA
        || Math.abs(e.clientY - inicio.y) > TOLERANCIA;
      if (longe) parar();
    },
    onPointerUp: parar,
    // Cancelar o cronômetro aqui continua certo: `pointercancel` também
    // vem quando a rolagem começa. Quem garante o gesto agora é o
    // `contextmenu`, que chega mesmo depois deste cancelamento.
    onPointerCancel: parar,
    onContextMenu: (e) => {
      // O menu nativo já é barrado no app inteiro (`lib/nativo.ts`); aqui
      // o `preventDefault` é para o caso de este componente ser usado
      // fora daquele guarda-chuva.
      e.preventDefault();
      abrir();
    },
    onClick: (e) => {
      if (!segurou.current) return;
      // O clique nasce do MESMO gesto que já virou reação.
      e.preventDefault();
      e.stopPropagation();
      segurou.current = false;
    },
  };
}
